import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { prepacksApi } from '@/services/api'
import { normalizePrepack } from './prepackCardModel'
import { canDeletePack } from './prepackLibraryModel'
import { latestCopy } from './prepackOverridesModel'
import { usePackDelete } from './usePackDelete'
import { usePackLifecycle } from './usePackLifecycle'

/**
 * The page a pack list is on (lifted from the Library for #1080). The page
 * belongs to the query it was turned in: when `context` (the organization, a
 * filter, the settled search, the sort) changes, the list is on its first
 * page in the same render, so a stale page is never requested.
 */
export function useListPage(context) {
  const [paging, setPaging] = useState({ page: 0, context })
  const page = paging.context === context ? paging.page : 0
  const setPage = useCallback((next) => setPaging({ page: next, context }), [context])
  return { page, setPage }
}

/**
 * A delete can leave the current page past the end; this steps back to the
 * last page once the list has answered. Returns the list's total and its
 * page count.
 */
export function useLastPageGuard(listQuery, { page, setPage, pageSize }) {
  const total = listQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const lastPage = pageCount - 1
  useEffect(() => {
    if (!listQuery.data || listQuery.isPlaceholderData || page <= lastPage) return
    setPage(lastPage)
  }, [listQuery.data, listQuery.isPlaceholderData, page, lastPage, setPage])
  return { total, pageCount }
}

/**
 * What a pack list and its drawer share (lifted from the Library for
 * #1080): the open pack, the selection across pages, the publish dialog,
 * and the lifecycle and delete flows. `packs` is the page on screen (normalized)
 * and `listQuery` its query; `invalidate()` marks the organization's pack
 * lists stale. A change of `slug` or `resetKey` closes the drawer, clears the
 * selection and closes the publish dialog.
 *
 * The open pack can leave the page (a new picture moves it up the
 * newest-first order): it is then read on its own, looking again while its
 * picture is generating, like the page. A pack deleted meanwhile (404) keeps
 * what the drawer opened with. `drawerPack` is the open pack as the latest
 * page, or its own read, has it, so the drawer's card shows a picture
 * generated since it opened; a save the drawer made (a Reset, #1089) is
 * newer than both until the page reads again, and `openSaved(saved)` opens
 * the pack it answered with.
 */
export function usePackList({ slug, packs, listQuery, canConfigure, canPublish, invalidate, resetKey = null }) {
  // Mirrors the API: deleting needs configure, and a published pack also needs publish.
  const canDelete = (pack) => canDeletePack(pack, { canConfigure, canPublish })
  const [openPack, setOpenPack] = useState(null)
  // The selection spans pages, so it keeps each pack, not just its id:
  // bulk delete needs the status, surfaces and recipe of off-page packs.
  const [selectedPacks, setSelectedPacks] = useState(() => new Map())
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  useEffect(() => { setOpenPack(null); setSelectedPacks(new Map()); setPublishDialogOpen(false) }, [slug, resetKey])
  const selectedPackIds = useMemo(() => Array.from(selectedPacks.keys()), [selectedPacks])
  const selectedBlocked = Array.from(selectedPacks.values()).filter((pack) => !canDelete(pack)).length

  const openPackId = openPack?.id
  const pagePack = openPackId ? packs.find((pack) => pack.id === openPackId) : undefined
  const openPackQuery = useQuery({
    queryKey: ['prepacks', 'library', slug, 'pack', openPackId],
    queryFn: ({ signal }) => prepacksApi.getPrepack(slug, openPackId, { signal }),
    enabled: Boolean(slug && openPackId && listQuery.data && !listQuery.isPlaceholderData && !pagePack),
    select: normalizePrepack,
    refetchInterval: (query) => (query.state.data?.banner_status === 'painting' ? 15000 : false),
  })
  const drawerPack = openPack ? latestCopy([pagePack, openPackQuery.isError ? null : openPackQuery.data, openPack]) : null
  const openSaved = useCallback((saved) => setOpenPack(normalizePrepack(saved)), [])

  const toggle = (pack) => {
    setSelectedPacks((current) => {
      const next = new Map(current)
      if (next.has(pack.id)) next.delete(pack.id)
      else next.set(pack.id, pack)
      return next
    })
  }
  const toggleAll = (selectAll) => {
    setSelectedPacks((current) => {
      const next = new Map(current)
      if (!selectAll) {
        packs.forEach((pack) => next.delete(pack.id))
      } else {
        packs.forEach((pack) => next.set(pack.id, pack))
      }
      return next
    })
  }
  const publishOne = (pack) => {
    setSelectedPacks(new Map([[pack.id, pack]]))
    setPublishDialogOpen(true)
  }

  const lifecycle = usePackLifecycle({ slug, invalidate, setOpenPack })
  const packDelete = usePackDelete({ slug, canDelete, selectedPacks, setSelectedPacks, invalidate, setOpenPack })

  return {
    slug,
    canDelete,
    openPack,
    setOpenPack,
    openSaved,
    drawerPack,
    selectedPacks,
    setSelectedPacks,
    selectedPackIds,
    selectedBlocked,
    toggle,
    toggleAll,
    publishOne,
    publishDialogOpen,
    setPublishDialogOpen,
    lifecycle,
    packDelete,
  }
}
